import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Chip,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Replay as ReplayIcon,
  ArrowBackOutlined as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import IntegrationSetup from '../integrations/IntegrationSetup';
import OnboardingStarterExplorer from './OnboardingStarterExplorer';
import PromptDialog from './prompt-display/PromptDialog';
import { getPrompt } from '../../services/api';
import Logo from '../layout/Logo';
import { ASSISTANT_COMPOSER_SURFACE, ASSISTANT_CONVERSATION_COLUMN_SX } from './prompt-display/chatPanelSurfaces';

const ASSISTANT_ONBOARDING_GIF = `${process.env.PUBLIC_URL}/onboarding/assistant-onboarding.gif`;
export const OPEN_ONBOARDING_EVENT = 'promptcrew:open-onboarding';
const suggestionChipSx = {
  maxWidth: '100%', height: 'auto', minHeight: 36, borderRadius: 999,
  fontWeight: 500, borderColor: 'rgba(15, 23, 42, 0.1)',
  '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 1, lineHeight: 1.5 },
};

/**
 * First-login guide with a choice of web and integration onboarding paths.
 */
const WelcomeWizard = ({ open, onClose, onExplore, user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { updateUserProfileData } = useAuth();
  const [isMarkingAsViewed, setIsMarkingAsViewed] = useState(false);
  const [gifFailed, setGifFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState('choice');
  const [exampleIndex, setExampleIndex] = useState(0);
  const [task, setTask] = useState('');
  const [lastCapability, setLastCapability] = useState(null);
  const savedScroll = useRef(0);
  const [copyNotice, setCopyNotice] = useState(null);
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [previewVariables, setPreviewVariables] = useState({});
  const [openingCapability, setOpeningCapability] = useState(false);
  useEffect(() => {
    if (!open) return;
    setView('choice');
    setExampleIndex(0);
    setSelectedCapability(null);
    setExpanded(false);
    setCopyNotice(null);
  }, [open]);
  const openCapability = async (prompt) => {
    if (openingCapability) return;
    setOpeningCapability(true);
    try {
      const fullPrompt = await getPrompt(prompt.id);
      setPreviewVariables({});
      savedScroll.current = contentRef.current?.scrollTop || 0;
      setLastCapability(fullPrompt);
      setSelectedCapability(fullPrompt);
    } catch (error) {
      setCopyNotice({ severity: 'error', message: error.message || 'Could not open this capability. Please try again.' });
    } finally {
      setOpeningCapability(false);
    }
  };
  const contentRef = useRef(null);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [view, exampleIndex]);
  const demoImage = useRef(null);
  const demoCanvas = useRef(null);
  const [paused, setPaused] = useState(false);
  const [demoVersion, setDemoVersion] = useState(0);
  const toggleDemo = () => {
    if (!paused) {
      const img = demoImage.current;
      const canvas = demoCanvas.current;
      if (!img?.naturalWidth || !canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
    }
    setPaused(!paused);
  };

  const handleClose = async (options = {}) => {
    if (!isMarkingAsViewed && user?.id) {
      setIsMarkingAsViewed(true);
      try {
        await updateUserProfileData({
          viewedHelpers: {
            introWizard: true,
          },
        });
      } catch (error) {
        console.error('Failed to mark welcome wizard as viewed:', error);
      } finally {
        setIsMarkingAsViewed(false);
      }
    }
    if (options.prompt) {
      try { window.sessionStorage.setItem('promptcrew.assistantLandingDraft', options.prompt); } catch { /* The mounted composer also receives the example event. */ }
    }
    onClose({ openAssistant: options.openAssistant === true, prompt: options.prompt || '' });
  };

  const handleExplore = async () => {
    await handleClose({ explore: true });
    onExplore?.();
  };

  const examples = ['Capability Library', 'Your Assistant'];
  const taskExamples = ['Summarize meeting notes and highlight next steps.', 'Draft a clear reply to a customer email.', 'Turn research notes into a concise brief.'];
  const returnToStarters = () => {
    setSelectedCapability(null);
    requestAnimationFrame(() => { if (contentRef.current) contentRef.current.scrollTop = savedScroll.current; });
  };

  return (
    <>
    <Dialog
      open={open && !selectedCapability}
      keepMounted
      onClose={handleClose}
      aria-labelledby="welcome-title"
      aria-describedby="welcome-description"
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: isMobile ? '100vw' : view === 'choice' ? 'min(1040px, calc(100vw - 32px))' : view === 'integrations' ? 'min(960px, 94vw)' : 'min(1200px, 94vw)',
          maxWidth: isMobile ? '100vw' : view === 'choice' ? '1040px' : view === 'integrations' ? '960px' : '1200px',
          height: isMobile ? '100dvh' : view === 'choice' ? 'auto' : view === 'integrations' ? 'min(620px, 92dvh)' : 'min(720px, 92dvh)',
          maxHeight: isMobile ? '100dvh' : view === 'choice' ? 'calc(100dvh - 32px)' : '92dvh',
          m: isMobile ? 0 : view === 'choice' ? 2 : 1,
          display: 'flex',
          flexDirection: 'column',
          '& .MuiTypography-root': { letterSpacing: 0 },
          '& .MuiButton-root': { minHeight: 40, textTransform: 'none', letterSpacing: 0 },
        },
      }}
    >
      <DialogTitle id="welcome-header" component="div" sx={{ pt: { xs: 2, sm: 3 }, pb: 2, pl: { xs: 3, md: 5 }, pr: 8, position: 'relative', flexShrink: 0 }}>
        <IconButton aria-label="Close onboarding" disabled={isMarkingAsViewed} onClick={() => handleClose()} sx={{ position: 'absolute', top: 12, right: 12 }}><CloseIcon /></IconButton>
        {view === 'choice' ? (
          <>
            <Box sx={{ mb: 2 }}><Logo size="medium" /></Box>
            <Typography id="welcome-title" component="div" sx={{ fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', lineHeight: 1.35, letterSpacing: 0 }}>
              Make your best AI work reusable
            </Typography>
            <Typography id="welcome-description" variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6, fontSize: '1rem' }}>
              A capability is a reusable set of AI instructions for a task you do repeatedly.
            </Typography>
          </>
        ) : view === 'app' ? (
          <>
            <Typography id="welcome-title" component="div" sx={{ fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem' }}>Explore Prompt<Box component="span" sx={{ color: 'primary.main' }}>Crew</Box></Typography>
            <Typography id="welcome-description" color="text.secondary" sx={{ mt: 1 }}>Find useful capabilities, then make them your own.</Typography>
          </>
        ) : (
          <>
            <Typography id="welcome-title" component="div" sx={{ fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', lineHeight: 1.35 }}>
              Prompt<Box component="span" sx={{ color: 'primary.main' }}>Crew</Box> in your AI tool
            </Typography>
            <Typography id="welcome-description" variant="body1" color="text.secondary" sx={{ mt: 1, fontSize: '1rem', lineHeight: 1.6 }}>
              Your PromptCrew capabilities, where you already work.
            </Typography>
          </>
        )}
      </DialogTitle>

      <DialogContent ref={contentRef} sx={{ pt: 1, px: { xs: 3, md: 5 }, minHeight: 0, overflowY: 'auto' }}>
        {view === 'choice' && (
          <Box sx={{ pt: 1, pb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {[
                {
                  view: 'app',
                  title: 'Explore PromptCrew',
                  description: 'Find capabilities in your Library and create or refine them with your Assistant.',
                  action: 'Explore PromptCrew',
                  bestFor: 'Best for getting started with guided examples.',
                },
                {
                  view: 'integrations',
                  title: 'Use your AI tool',
                  description: 'Bring your PromptCrew capabilities into the AI tool you already use.',
                  action: 'Connect your AI tool',
                  bestFor: 'Best for people already working in ChatGPT, Claude, or another AI tool.',
                },
              ].map((path) => (
                <Button
                  key={path.view}
                  aria-label={path.title}
                  variant="outlined"
                  onClick={() => setView(path.view)}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                    justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none',
                    color: 'text.primary', p: { xs: 2, sm: 3 }, borderRadius: 2,
                    borderColor: 'divider', minWidth: 0, letterSpacing: 0,
                    transition: 'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(29, 78, 216, 0.025)', boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)' },
                    '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 3 },
                  }}
                >
                  {path.view === 'app' ? (
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.875 }}>
                      <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.4 }}>
                        Explore Prompt<Box component="span" sx={{ color: 'primary.main' }}>Crew</Box>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.4 }}>
                      Use your AI tool
                    </Typography>
                  )}
                  <Typography component="span" sx={{ display: 'block', mt: 1, mb: 3, fontSize: '1rem', lineHeight: 1.6, color: 'text.primary' }}>
                    {path.description}
                  </Typography>
                  <Typography component="span" sx={{ display: 'block', mb: 2, fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary' }}>{path.bestFor}</Typography>
                  <Box component="span" sx={{ mt: 'auto', pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, color: 'primary.main' }}>
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{path.action}</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        )}
        {view === 'app' && <Box>
          <Tabs value={exampleIndex} onChange={(_, value) => setExampleIndex(value)} variant="scrollable" scrollButtons="auto" aria-label="Explore PromptCrew workflows" sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', letterSpacing: 0, minHeight: 48 } }}>
            {examples.map((label, index) => <Tab key={label} label={label} id={`welcome-tab-${index}`} aria-controls={`welcome-panel-${index}`} />)}
          </Tabs>
          <Box role="tabpanel" id="welcome-panel-0" aria-labelledby="welcome-tab-0" hidden={exampleIndex !== 0} sx={{ py: 3 }}>
            <OnboardingStarterExplorer onOpenCapability={openCapability} onExploreLibrary={handleExplore} onExploreAssistant={() => setExampleIndex(1)} />
            {openingCapability && <Typography role="status" sx={{ mt: 1 }}>Opening capability...</Typography>}
          </Box>
          <Box role="tabpanel" id="welcome-panel-1" aria-labelledby="welcome-tab-1" hidden={exampleIndex !== 1} sx={{ ...ASSISTANT_CONVERSATION_COLUMN_SX, py: 3 }}>
            <Typography component="div" sx={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 600, mb: 3 }}>Prompt<Box component="span" sx={{ color: 'primary.main' }}>Crew</Box> Assistant</Typography>
            <Typography component="h2" sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 1 }}>What would you like to make easier?</Typography>
            <Typography sx={{ lineHeight: 1.65, mb: 3 }}>Your Assistant helps with your task, then turns what works into a capability you can reuse. Try it on your work, refine it together, and save it for next time.</Typography>
            <Box sx={{ border: 1, borderColor: ASSISTANT_COMPOSER_SURFACE.borderColor, bgcolor: ASSISTANT_COMPOSER_SURFACE.bgcolor, boxShadow: ASSISTANT_COMPOSER_SURFACE.boxShadow, borderRadius: 2.5, p: 2, '&:focus-within': { borderColor: ASSISTANT_COMPOSER_SURFACE.focusBorderColor, boxShadow: ASSISTANT_COMPOSER_SURFACE.focusBoxShadow } }}>
              <TextField label="What task would you like help with?" placeholder="Describe your work and the result you want. For example: turn meeting notes into decisions and follow-ups." InputLabelProps={{ shrink: true }} variant="standard" InputProps={{ disableUnderline: true }} multiline minRows={3} maxRows={6} fullWidth value={task} onChange={(event) => setTask(event.target.value)} sx={{ '& .MuiInputBase-root': { fontSize: '0.9375rem', lineHeight: 1.5 } }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 3 }}>
              {taskExamples.map((text) => <Chip key={text} label={text} variant="outlined" onClick={() => setTask(text)} sx={suggestionChipSx} />)}
              {lastCapability && <Chip label={`Adapt ${lastCapability.title}`} variant="outlined" sx={suggestionChipSx} onClick={() => setTask(`Help me adapt "${lastCapability.title}" to my work. Fetch the full PromptCrew capability at ${new URL('/library/' + encodeURIComponent(lastCapability.id), window.location.origin)} before we begin.`)} />}
            </Box>
            <Button onClick={() => { setExpanded(true); setPaused(false); }} sx={{ display: 'flex', mt: 2 }}>Watch a short demo</Button>
          </Box>
        </Box>}
        {view === 'integrations' && (
          <IntegrationSetup compact onboardingCapability={user?.onboardingCapabilities?.[0]} onboardingCapabilities={user?.onboardingCapabilities || []} />
        )}
      </DialogContent>

      {view !== 'choice' && <DialogActions sx={{ px: { xs: 2, md: 5 }, py: 2, pb: 'max(16px, env(safe-area-inset-bottom))', gap: 1, flexWrap: 'wrap', minHeight: 68, boxSizing: 'border-box', flexShrink: 0, justifyContent: 'space-between' }}>
        {view === 'app' && <>
          <Button startIcon={<BackIcon />} onClick={() => setView('choice')} disabled={isMarkingAsViewed}>All options</Button>
          {exampleIndex === 1 && <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={!task.trim() || isMarkingAsViewed} onClick={() => handleClose({ openAssistant: true, prompt: task.trim() })}>Start with the Assistant</Button>}
        </>}
        {view === 'integrations' && <Button startIcon={<BackIcon />} onClick={() => setView('choice')} disabled={isMarkingAsViewed}>All options</Button>}
      </DialogActions>}
      <Snackbar open={Boolean(copyNotice)} autoHideDuration={3000} onClose={() => setCopyNotice(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={copyNotice?.severity || 'success'} variant="filled" onClose={() => setCopyNotice(null)}>{copyNotice?.message}</Alert>
      </Snackbar>

      <Dialog
        open={expanded}
        onClose={(e) => {
          e?.stopPropagation?.();
          setExpanded(false);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: 'min(1600px, 96vw)',
            maxWidth: '1600px',
            height: '92dvh',
            maxHeight: '92dvh',
            m: 1,
          },
        }}
        sx={{ zIndex: (themeValue) => themeValue.zIndex.modal + 20 }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">PromptCrew Assistant</Typography>
          <IconButton aria-label="Close expanded gif" onClick={() => setExpanded(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, minHeight: 0, flex: 1, display: 'flex', bgcolor: 'background.paper', overflow: 'hidden' }}>
          {gifFailed && <Typography>Could not load the walkthrough.</Typography>}
          <Box
            component="img"
            ref={demoImage}
            key={demoVersion}
            src={`${ASSISTANT_ONBOARDING_GIF}?replay=${demoVersion}`}
            onError={() => setGifFailed(true)}
            alt="Expanded PromptCrew Assistant"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: paused || gifFailed ? 'none' : 'block',
              borderRadius: 1,
            }}
          />
          <Box component="canvas" ref={demoCanvas} aria-label="Paused walkthrough" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: paused ? 'block' : 'none' }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', flexShrink: 0, px: 3 }}>
          <Button startIcon={paused ? <PlayIcon /> : <PauseIcon />} onClick={toggleDemo} disabled={gifFailed}>{paused ? 'Play' : 'Pause'}</Button>
          <Button startIcon={<ReplayIcon />} onClick={() => { setDemoVersion((value) => value + 1); setPaused(false); setGifFailed(false); }}>Replay</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
    {selectedCapability && <PromptDialog closeLabel="Back to your starters" open={open} prompt={selectedCapability} variableValues={previewVariables} onVariableChange={(name, value) => setPreviewVariables((current) => ({ ...current, [name]: value }))} onClose={returnToStarters} showEditButton={false} showCloneButton={false} showShareButton={false} onPromptUpdated={setSelectedCapability} />}
    </>
  );
};

export default WelcomeWizard;
